/**
 * Steps of a resistivity job for the Workflow panel (components/map/WorkflowPanel.js).
 * Pure module: derives each step's state from the survey dataset row, the project and the
 * inversion models linked to the dataset. No React.
 *
 * state: 'done' | 'pending' | 'failed' | 'blocked' | 'optional' | 'available'
 */

import { isVectorFile } from './dataset-kind';

export const WORKFLOW_KEYS = ['upload', 'qc', 'georef', 'topo', 'inversion', 'import', 'coords', 'view', 'report'];

const FORMAT_LABELS = { stg: 'SuperSting .stg', stn: 'Syscal .stn', ohm: 'ABEM .ohm', res2dinv: 'RES2DINV .dat' };
const plural = (n, w) => `${n} ${w}${n === 1 ? '' : 's'}`;

function uploadDetail(dataset) {
  const m = dataset.metadata || {};
  const bits = [dataset.name || 'Survey'];
  if (m.format) bits.push(FORMAT_LABELS[m.format] || m.format);
  if (m.n_electrodes) bits.push(plural(m.n_electrodes, 'electrode'));
  return bits.join(' · ');
}

/** Short summary of metadata.qc (A-D tier counts, % tier D); '' when the dataset has none. */
export function qcSummary(dataset) {
  const q = dataset?.metadata?.qc;
  const c = q && typeof q === 'object' ? q.counts : null;
  if (!c) return '';
  const tiers = ['A', 'B', 'C', 'D'].filter((t) => typeof c[t] === 'number').map((t) => `${t} ${c[t]}`);
  if (!tiers.length) return '';
  const total = q.n_readings ?? ['A', 'B', 'C', 'D'].reduce((n, t) => n + (c[t] || 0), 0);
  const pctD = typeof q.pct_d === 'number' ? q.pct_d : (total ? ((c.D || 0) / total) * 100 : null);
  return [tiers.join(' · '), pctD == null ? '' : `${pctD.toFixed(1)}% tier D`].filter(Boolean).join(' · ');
}

/** The survey the Workflow panel follows: the dataset selected in Layers, else ?dataset=. */
export function workflowDatasetId(selectedDatasetId, urlDatasetId) {
  return selectedDatasetId || urlDatasetId || null;
}

function qcStep(dataset) {
  const s = String(dataset.qc_status || '').toLowerCase();
  const sum = qcSummary(dataset);
  const withSum = (d) => (sum ? `${d} (${sum})` : d);
  if (s === 'passed') return { state: 'done', detail: withSum('QC passed') };
  if (s === 'failed') return { state: 'failed', detail: withSum('QC failed: review the flagged readings') };
  return { state: 'pending', detail: withSum(s ? `QC ${s}` : 'QC not run yet') };
}

function georefStep(dataset) {
  const g = dataset.metadata?.georef;
  if (g && g.mode !== 'local') return { state: 'done', detail: g.crs ? `CRS ${g.crs}` : 'Georeferenced' };
  // A GeoJSON / CSV / KML file carries geographic coordinates already (no "Local coordinates" banner).
  if (isVectorFile(dataset)) return { state: 'done', detail: 'Geographic coordinates from the file' };
  return { state: 'pending', detail: 'Local coordinates: place the survey on the map' };
}

function topoStep(dataset, project) {
  const t = dataset.metadata?.topography;
  if (t) {
    const when = t.created_at ? String(t.created_at).slice(0, 10) : '';
    return { state: 'done', detail: [t.sampling ? `Sampled ${t.sampling}` : 'Topography stored', when].filter(Boolean).join(' · ') };
  }
  if (project && !project.metadata?.dem_dataset_id) return { state: 'pending', detail: 'Mark a raster as the project DEM (Datasets page)' };
  return { state: 'pending', detail: 'Sample the project DEM along the line' };
}

/** Stored models of the dataset plus browser-only models of the same dataset, without double counting. */
function linkedModels(datasetId, storedModels, uiModels) {
  const stored = (storedModels || []).filter((m) => !m.dataset_id || m.dataset_id === datasetId);
  const ids = new Set(stored.map((m) => m.id));
  const local = (uiModels || []).filter((m) => m.datasetId === datasetId && !ids.has(m.id));
  return { stored, local };
}

/**
 * @param {{dataset: object|null, project?: object|null, storedModels?: object[], uiModels?: object[]}} input
 *   dataset: the datasets row ({ id, name, qc_status, metadata }); null when no survey is open.
 *   storedModels: inversionApi.list items ({ id, dataset_id, georeferenced }).
 *   uiModels: the map panel's models ({ id, datasetId }).
 */
export function buildWorkflowSteps({ dataset, project = null, storedModels = [], uiModels = [] } = {}) {
  const step = (key, title, state, detail) => ({ key, title, state, detail });
  if (!dataset) {
    const blocked = 'Open a survey first';
    return [
      step('upload', 'Upload', 'pending', 'No survey open: open one or upload a file'),
      step('qc', 'QC', 'blocked', blocked),
      step('georef', 'Georeference', 'blocked', blocked),
      step('topo', 'Topography', 'blocked', blocked),
      step('inversion', 'Inversion in EarthImager', 'blocked', blocked),
      step('import', 'Import result', 'blocked', blocked),
      step('coords', 'Coordinates', 'blocked', blocked),
      step('view', 'View', 'blocked', blocked),
      step('report', 'Report', 'blocked', blocked),
    ];
  }
  const { stored, local } = linkedModels(dataset.id, storedModels, uiModels);
  const total = stored.length + local.length;
  const qc = qcStep(dataset);
  const georef = georefStep(dataset);
  const topo = topoStep(dataset, project);

  const importStep = total
    ? { state: 'done', detail: plural(total, 'result') }
    : { state: 'pending', detail: 'Import the .out result of the inversion' };
  const inversion = total
    ? { state: 'done', detail: 'Result imported' }
    : { state: 'pending', detail: 'Run the inversion with the .stg and .trn, then import the .out' };

  let coords;
  if (!total) coords = { state: 'blocked', detail: 'Import a result first' };
  else if (!stored.length) coords = { state: 'pending', detail: 'Save the result to a project, then define coordinates' };
  else {
    const without = stored.filter((m) => m.georeferenced !== true).length;
    coords = without
      ? { state: 'pending', detail: `${without} of ${stored.length} models without coordinates` }
      : { state: 'done', detail: `${plural(stored.length, 'model')} georeferenced` };
  }

  return [
    step('upload', 'Upload', 'done', uploadDetail(dataset)),
    step('qc', 'QC', qc.state, qc.detail),
    step('georef', 'Georeference', georef.state, georef.detail),
    step('topo', 'Topography', topo.state, topo.detail),
    step('inversion', 'Inversion in EarthImager', inversion.state, inversion.detail),
    step('import', 'Import result', importStep.state, importStep.detail),
    step('coords', 'Coordinates', coords.state, coords.detail),
    step('view', 'View', total ? 'available' : 'blocked', total ? 'Section, pseudosection or 3D' : 'Import a result first'),
    step('report', 'Report', 'optional', 'Optional: build a report'),
  ];
}
